/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description Edit Eligibility Data
 */
import { Link } from 'react-router-dom';
import { Write } from '@icon-park/react';
import { Button as AntButton, ButtonProps, App, Tooltip } from 'antd';

import { MODAL_TYPES } from '../modal';
import { getPayerMeta } from 'utils/eligibility/eligibilityResponse';
import { getEditClaimEligibilityRequestPath, getEditEligibilityRequestPath } from 'utils/router';
import { useAppStoreValues } from 'store/app';

const commonProps: ButtonProps = { color: 'default' };

const getModalContent = (isArchivedPayer: boolean, isInValidNpi: boolean, isValidLocation: boolean): string => {
	if (isArchivedPayer && isInValidNpi) {
		return "Can't edit this request anymore as this payer is archived and the NPI is disabled or has not completed eKYC verification yet.";
	} else if (isArchivedPayer) {
		return "Can't edit this request anymore as this payer is archived.";
	} else if (!isValidLocation) {
		return "Can't edit this request anymore as this location is disabled.";
	} else {
		return "Can't edit this request anymore as this NPI is disabled or has not completed eKYC verification yet.";
	}
};

/**
 * EditEligibilityRequest
 *
 */
const EditEligibilityRequest: React.FC<
	{
		id: string;
		payer?: Payers.ISelectedPayer;
		providerMeta?: Metadata.IProviderMeta;
		isValidLocation: boolean;
		label?: React.ReactNode;
	} & ButtonProps
> = (props) => {
	const { modal } = App.useApp();
	const { id, payer, providerMeta, isValidLocation, label, ...restProps } = props;
	const combinedProps = { ...commonProps, ...restProps };
	const isArchivedPayer = getPayerMeta(payer).isArchived;
	const isInValidNpi = !providerMeta?.canMakeRequests;
	const { isARCustomer } = useAppStoreValues().userAccess;

	const handleClick = () => {
		modal.error({
			...MODAL_TYPES.warning,
			width: 450,
			content: getModalContent(isArchivedPayer, isInValidNpi, isValidLocation),
		});
	};

	// in case of network error response id can be null
	if (!id) {
		return null;
	}

	const isInvalid = isArchivedPayer || isInValidNpi || !isValidLocation;

	const button = label ? (
		<AntButton {...combinedProps} onClick={isInvalid ? handleClick : undefined}>
			{label}
		</AntButton>
	) : (
		<Tooltip title='Edit Request'>
			<AntButton {...combinedProps} onClick={isInvalid ? handleClick : undefined} icon={<Write />} />
		</Tooltip>
	);

	if (isInvalid) {
		return button;
	}

	return (
		<Link {...restProps} to={isARCustomer ? getEditClaimEligibilityRequestPath(id) : getEditEligibilityRequestPath(id)}>
			{button}
		</Link>
	);
};

export default EditEligibilityRequest;
